import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appServicesAPI, clustersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ImageSelector from '../../components/ImageSelector';
import useProvisionSettle from '../../hooks/useProvisionSettle';

const STATUS_VARIANT = {
  running:          '',
  pending:          'paused',
  failed:           'error',
  CrashLoopBackOff: 'error',
  OOMKilled:        'error',
  ImagePullBackOff: 'error',
  Error:            'error',
  unknown:          'draft',
};
const CRASH_REASONS = ['CrashLoopBackOff', 'OOMKilled', 'Error', 'ImagePullBackOff', 'ErrImagePull'];

function StatusBadge({ status, reason, restarts }) {
  const key = reason && CRASH_REASONS.includes(reason) ? reason : (status || 'unknown').toLowerCase();
  const variant = key in STATUS_VARIANT ? STATUS_VARIANT[key] : STATUS_VARIANT.unknown;
  const label = reason || status || 'unknown';
  return (
    <span className={`status${variant ? ' ' + variant : ''}`}>
      <span className="pulse" />
      {label}
      {restarts > 0 && <span style={{ opacity: 0.7, fontSize: 10 }}>({restarts}↺)</span>}
    </span>
  );
}

export default function InstanceDetailPage() {
  const { hasSectionAction } = useAuth();
  const canEdit = hasSectionAction('workloads', 'edit');
  const canDelete = hasSectionAction('workloads', 'delete');
  const { id } = useParams();
  const navigate = useNavigate();

  const [instance, setInstance]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [tab, setTab]               = useState('overview'); // overview | logs | terminal | edit | yaml
  const [liveStatus, setLiveStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [clusters, setClusters]     = useState([]);
  // Update-image state (raw Pod: recreate from the new image — not a rolling update)
  const [newImage, setNewImage]     = useState('');
  const [updatingImg, setUpdatingImg] = useState(false);
  const [imgMsg, setImgMsg]         = useState(null); // { type: 'ok'|'err'|'progress', text }

  // Logs state
  const [logs, setLogs]             = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError]   = useState('');
  const [tail, setTail]             = useState(100);
  const [previous, setPrevious]     = useState(false);
  const logsRef = useRef();

  // YAML history state
  const [manifestHistory, setManifestHistory] = useState(null); // null = not loaded yet
  const [manifestHistoryLoading, setManifestHistoryLoading] = useState(false);

  // Terminal state
  const [termInput, setTermInput]   = useState('');
  const [termHistory, setTermHistory] = useState([]);
  const [termRunning, setTermRunning] = useState(false);
  const termBottomRef = useRef();

  // Edit state
  const [editForm, setEditForm]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveMsg, setSaveMsg]       = useState('');

  const loadInstance = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [svcRes, clRes] = await Promise.all([
        appServicesAPI.get(id),
        clustersAPI.list(),
      ]);
      setInstance(svcRes.data);
      setClusters(clRes.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load instance');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await appServicesAPI.liveStatus(id);
      setLiveStatus(r.data);
    } catch (e) {}
    finally { setStatusLoading(false); }
  }, [id]);

  useEffect(() => { loadInstance(); }, [loadInstance]);
  useEffect(() => { if (tab === 'overview') loadStatus(); }, [tab, loadStatus]);

  // A YAML launch redirects here before its apply has run — keep watching until it
  // lands, so a rejected manifest shows up as an error instead of "provisioning" forever.
  useProvisionSettle(id, instance?.status, (row) => {
    setInstance(prev => ({ ...prev, status: row.status, notes: row.notes, name: row.name }));
    if (!['provisioning', 'pending'].includes((row.status || '').toLowerCase())) loadStatus();
  });

  // Logs
  const fetchLogs = useCallback(async (t = tail, prev = previous) => {
    setLogsLoading(true); setLogsError(''); setLogs('');
    try {
      const r = await appServicesAPI.getLogs(id, null, t, prev);
      setLogs(r.data.logs || '');
      if (r.data.error) setLogsError(r.data.error);
    } catch (e) {
      setLogsError(e.response?.data?.detail || 'Failed to fetch logs');
    } finally { setLogsLoading(false); }
  }, [id, tail, previous]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs(tail, previous);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs]);

  // YAML history — fetched once per tab visit, not re-fetched on every render
  useEffect(() => {
    if (tab !== 'yaml' || manifestHistory !== null) return;
    setManifestHistoryLoading(true);
    appServicesAPI.getManifestHistory(id)
      .then(r => setManifestHistory(r.data?.manifests || []))
      .catch(() => setManifestHistory([]))
      .finally(() => setManifestHistoryLoading(false));
  }, [tab, id, manifestHistory]);

  // Terminal
  const handleTermExec = async () => {
    if (!termInput.trim()) return;
    const cmd = termInput.trim();
    setTermInput('');
    setTermHistory(h => [...h, { type: 'input', text: `$ ${cmd}` }]);
    setTermRunning(true);
    try {
      const r = await appServicesAPI.execInService(id, cmd);
      if (r.data.stdout) setTermHistory(h => [...h, { type: 'output', text: r.data.stdout }]);
      if (r.data.stderr) setTermHistory(h => [...h, { type: 'error',  text: r.data.stderr }]);
      if (!r.data.stdout && !r.data.stderr) setTermHistory(h => [...h, { type: 'output', text: r.data.note || '(no output)' }]);
      // A streaming command (ping/top/tail -f) is bounded server-side; tell the user
      // its output was cut off at the limit rather than looking like it just stopped.
      if (r.data.bounded_seconds && r.data.stdout) {
        setTermHistory(h => [...h, { type: 'output', text: `… (output stopped after ${r.data.bounded_seconds}s — this terminal isn't interactive; add a limit like \`ping -c 4 host\`)` }]);
      }
    } catch (e) {
      setTermHistory(h => [...h, { type: 'error', text: e.response?.data?.detail || 'Command failed' }]);
    } finally { setTermRunning(false); }
  };

  useEffect(() => {
    if (termBottomRef.current) termBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [termHistory]);

  // Edit / save
  useEffect(() => {
    if (instance && !editForm) {
      setEditForm({
        image:          instance.image,
        cpu_request:    instance.cpu_request,
        cpu_limit:      instance.cpu_limit,
        mem_request_gb: instance.mem_request_gb,
        mem_limit_gb:   instance.mem_limit_gb,
        storage_gb:     instance.storage_gb || '',
      });
    }
  }, [instance, editForm]);

  const handleSaveEdit = async () => {
    setSaving(true); setSaveMsg('');
    try {
      // Re-provision with new spec by calling create with same name
      // The backend does kubectl apply which updates the pod spec
      await appServicesAPI.restart(id);
      setSaveMsg('Restarted with updated spec. Note: pod spec changes require delete+recreate for raw pods.');
      loadInstance();
    } catch (e) {
      setSaveMsg(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete instance "${instance?.name}"? This will destroy the pod and all data inside.`)) return;
    try {
      await appServicesAPI.delete(id);
      navigate('/compute');
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleRestart = async () => {
    try {
      await appServicesAPI.restart(id);
      setTimeout(loadStatus, 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Restart failed'); }
  };

  // A raw Pod is NOT a rolling workload — update its image by re-creating with the
  // same identity (name/namespace/cluster). The backend re-uses the existing row,
  // re-applies the pod, and — crucially — only advances the row to status=running
  // with the NEW image when the apply genuinely succeeded on the cluster. On failure
  // it rolls the image back and sets status=failed with a reason in notes.
  //
  // So the request kicks off provisioning in the BACKGROUND; we must NOT block the UI
  // on it. We fire the request, then POLL the instance row until it settles:
  //   - image === requested AND status === running  → success
  //   - status === failed                            → show the backend's reason
  //   - timeout                                      → still applying, keep polling in bg
  const handleUpdateImage = async () => {
    const img = (newImage || '').trim();
    if (!img || !instance) { setImgMsg({ type: 'err', text: 'Pick or enter a new image' }); return; }
    if (img === instance.image) { setImgMsg({ type: 'err', text: 'That is already the current image' }); return; }
    if (!window.confirm(`Update "${instance.name}" to image "${img}"?\n\nThe pod will be recreated (brief downtime; ephemeral data is lost — persistent PVCs are kept).`)) return;

    setUpdatingImg(true);
    setImgMsg({ type: 'progress', text: 'Submitting…' });
    try {
      await appServicesAPI.create({
        cluster_id: instance.cluster_id,
        name: instance.name,
        display_name: instance.display_name || instance.name,
        image: img,
        kind: 'Pod',
        namespace: instance.namespace,
        replicas: 1,
        cpu_request: String(instance.cpu_request),
        cpu_limit: String(instance.cpu_limit),
        mem_request_gb: instance.mem_request_gb,
        mem_limit_gb: instance.mem_limit_gb,
        storage_gb: instance.storage_gb || null,
        env_vars: {}, annotations: {}, labels: instance.deploy_config?.labels || {},
        notes: JSON.stringify(instance.deploy_config || {}),
        // This IS an intentional update of an existing instance — opt in to the
        // upsert so the backend re-applies instead of 409ing on the name collision.
        allow_update: true,
      });
    } catch (e) {
      setImgMsg({ type: 'err', text: e.response?.data?.detail || 'Failed to submit image update' });
      setUpdatingImg(false);
      return;
    }

    // Submitted — provisioning now runs in the background. Poll for the real outcome
    // instead of freezing the screen. The button stays in "Applying…" but the page
    // is fully interactive.
    setImgMsg({ type: 'progress', text: `Applying ${img} — recreating pod…` });
    const started = Date.now();
    const poll = async () => {
      try {
        const r = await appServicesAPI.get(id);
        const s = r.data || {};
        if ((s.status || '').toLowerCase() === 'failed') {
          setInstance(s);
          setImgMsg({ type: 'err', text: s.notes || 'Image update failed on the cluster' });
          setUpdatingImg(false);
          return;
        }
        if (s.image === img && (s.status || '').toLowerCase() === 'running') {
          setInstance(s);
          setImgMsg({ type: 'ok', text: `Updated to ${img} ✓` });
          setNewImage('');
          loadStatus();
          setUpdatingImg(false);
          setTimeout(() => setImgMsg(null), 6000);
          return;
        }
      } catch (_) { /* transient — keep polling */ }
      if (Date.now() - started > 90000) {
        // Give up waiting in the foreground; leave it applying. Refresh once so the
        // user sees whatever state the row reached.
        loadInstance(); loadStatus();
        setImgMsg({ type: 'progress', text: 'Still applying — refresh in a moment to see the result.' });
        setUpdatingImg(false);
        return;
      }
      setTimeout(poll, 2500);
    };
    setTimeout(poll, 2500);
  };

  const handleDownloadLogs = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${instance?.name}-logs.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--muted-2)' }}>Loading instance...</div>;
  if (error)   return <div style={{ padding: 40, color: 'var(--color-danger-500)' }}>{error}</div>;
  if (!instance) return null;

  const clusterName = clusters.find(c => c.id === instance.cluster_id)?.name || `cluster-${instance.cluster_id}`;
  // Prefer live cluster resources over the stored 1/2 defaults; "None" when the pod declares none.
  const _lr = instance.live_resources;
  const _reqLim = (req, lim) => (!req && !lim) ? 'None' : `${req || '—'} req → ${lim || '—'} lim`;
  const cpuDisplay = _lr ? _reqLim(_lr.cpu_req, _lr.cpu_lim) : `${instance.cpu_request} req → ${instance.cpu_limit} lim`;
  const ramDisplay = _lr ? _reqLim(_lr.mem_req, _lr.mem_lim) : `${instance.mem_request_gb}G req → ${instance.mem_limit_gb}G lim`;
  const pods = liveStatus?.pods || [];
  const primaryPod = pods[0];
  const overallStatus = liveStatus?.status || instance.status;
  const crashReason = pods.find(p => p.reason && CRASH_REASONS.includes(p.reason))?.reason;

  const TABS = [
    { id: 'overview', label: '⊞ Overview' },
    { id: 'logs',     label: '📋 Logs' },
    { id: 'terminal', label: '⬛ Terminal' },
    { id: 'edit',     label: '✏ Edit' },
    { id: 'yaml',     label: '📄 YAML History' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: 'var(--muted)' }}>
        <span style={{ cursor: 'pointer', color: 'var(--brand)' }} onClick={() => navigate('/compute')}>
          Compute
        </span>
        <span>›</span>
        <span style={{ cursor: 'pointer', color: 'var(--brand)' }} onClick={() => navigate('/compute?tab=instances')}>
          Instances
        </span>
        <span>›</span>
        <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{instance.name}</span>
      </div>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--brand-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
            🖥
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>{instance.name}</h1>
            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--muted)' }}>
              <span>{clusterName}</span>
              <span>·</span>
              <span>{instance.namespace}</span>
              <span>·</span>
              <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{instance.image}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusBadge
            status={overallStatus}
            reason={crashReason}
            restarts={pods.reduce((s, p) => s + (p.restarts || 0), 0)}
          />
          {canEdit && (
          <button onClick={handleRestart} className="btn btn-tinted-success btn-sm">
            ↺ Restart
          </button>
          )}
          {canDelete && (
          <button onClick={handleDelete} className="btn btn-tinted-danger btn-sm">
            🗑 Delete
          </button>
          )}
        </div>

        {/* Update image — recreate this Pod from a new image version */}
        {canEdit && (
        <div style={{ marginTop: 16, padding: 16, borderRadius: 10,
                      border: '1px solid var(--surface-2)', background: 'var(--surface-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-1)' }}>Update image</span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              pod is recreated — brief downtime, PVCs kept
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12,
                        fontSize: 12, color: 'var(--ink-2)' }}>
            <span style={{ color: 'var(--ink-3)' }}>current:</span>
            <code style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 6px', borderRadius: 4,
                           background: 'var(--surface-2)', color: 'var(--ink-1)' }}>
              {instance?.image || '—'}
            </code>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 300, flex: '1 1 300px', maxWidth: 520 }}>
              <ImageSelector value={newImage} onChange={setNewImage}
                placeholder="pick a new image version…" />
            </div>
            <button onClick={handleUpdateImage} disabled={updatingImg || !newImage}
              className="btn btn-primary">
              {updatingImg ? '⏳ Applying…' : '↑ Update & recreate'}
            </button>
          </div>
          {imgMsg && imgMsg.text && (
            <div style={{ marginTop: 10, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                          color: imgMsg.type === 'err' ? 'var(--color-danger-500)'
                               : imgMsg.type === 'ok'  ? 'var(--color-success-700)'
                               : 'var(--ink-2)' }}>
              {imgMsg.type === 'progress' && <span className="spinner-sm" style={{ display: 'inline-block' }}>⟳</span>}
              <span style={{ wordBreak: 'break-word' }}>{imgMsg.text}</span>
            </div>
          )}
        </div>
        )}
      </div>

      {instance.status === 'failed' && instance.notes && !crashReason && (
        <div className="banner danger" style={{ marginBottom: 16 }}>
          <span>Deploy failed: {instance.notes}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-underline" style={{ marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={tab === t.id ? 'active' : undefined}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* Config card */}
          <div className="card">
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 16 }}>Configuration</div>
            {[
              ['Image',       instance.image],
              ['CPU',         cpuDisplay],
              ['RAM',         ramDisplay],
              ['Storage',     instance.storage_gb
                ? `${instance.storage_gb}GB (${instance.storage_class ? `persistent · ${instance.storage_class}` : 'emptyDir'})`
                : 'None'],
              ['Namespace',   instance.namespace],
              ['Cluster',     clusterName],
              ['Created',     instance.created_at ? new Date(instance.created_at).toLocaleString() : '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                padding: '8px 0', borderBottom: '1px solid var(--surface-2)' }}>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 12, color: 'var(--ink)', fontFamily: label === 'Image' ? 'monospace' : 'inherit',
                  textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Live pod status card */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Live Status
              </div>
              <button onClick={loadStatus} disabled={statusLoading} className="btn btn-secondary btn-sm">
                {statusLoading ? '...' : '⟳ Refresh'}
              </button>
            </div>

            {!liveStatus && !statusLoading && (
              <div style={{ color: 'var(--muted-2)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                Click Refresh to load live status
              </div>
            )}

            {primaryPod && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', fontFamily: 'monospace' }}>
                      {primaryPod.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 2 }}>
                      Ready: {primaryPod.ready ? 'Yes' : 'No'} · Restarts: {primaryPod.restarts || 0}
                    </div>
                  </div>
                  <StatusBadge status={primaryPod.phase} reason={primaryPod.reason} restarts={primaryPod.restarts} />
                </div>
                {primaryPod.reason && CRASH_REASONS.includes(primaryPod.reason) && (
                  <div className="banner danger" style={{ marginTop: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Container is crashing. Check logs for details.</span>
                    <button onClick={() => { setTab('logs'); setPrevious(true); }}
                      className="btn btn-danger btn-sm">
                      View Logs →
                    </button>
                  </div>
                )}
              </div>
            )}

            {liveStatus?.events?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
                  letterSpacing: '0.05em', marginBottom: 8 }}>Recent Events</div>
                {liveStatus.events.slice(-5).map((ev, i) => (
                  <div key={i} style={{ fontSize: 11, color: 'var(--ink-2)', padding: '4px 0',
                    borderBottom: '1px solid var(--surface-2)', fontFamily: 'monospace' }}>
                    {ev}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LOGS TAB ── */}
      {tab === 'logs' && (
        <div style={{ background: '#0d1117', borderRadius: 10, overflow: 'hidden', height: 'calc(100vh - 280px)' }}>
          {/* Logs toolbar */}
          <div style={{ background: '#161b22', padding: '10px 16px', borderBottom: '1px solid #30363d',
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <select value={tail} onChange={e => { setTail(Number(e.target.value)); fetchLogs(Number(e.target.value), previous); }}
              style={{ background: '#21262d', color: '#e6edf3', border: '1px solid #30363d',
                borderRadius: 5, padding: '4px 8px', fontSize: 12 }}>
              {[50, 100, 200, 500, 1000].map(n => <option key={n} value={n}>Last {n}</option>)}
            </select>
            <button onClick={() => { const next = !previous; setPrevious(next); fetchLogs(tail, next); }}
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 5, cursor: 'pointer', fontWeight: 600,
                background: previous ? 'var(--ink-2)' : '#21262d',
                color: previous ? 'var(--color-warning-500)' : '#e6edf3',
                border: previous ? '1px solid var(--color-warning-500)' : '1px solid #30363d' }}>
              ⚡ Previous container
            </button>
            <button onClick={() => fetchLogs(tail, previous)}
              style={{ padding: '4px 12px', fontSize: 12, background: '#21262d', color: '#e6edf3',
                border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer' }}>
              ↺ Reload
            </button>
            <button onClick={handleDownloadLogs} disabled={!logs || logsLoading}
              style={{ padding: '4px 12px', fontSize: 12, background: '#21262d', color: '#e6edf3',
                border: '1px solid #30363d', borderRadius: 5, cursor: 'pointer',
                opacity: (!logs || logsLoading) ? 0.4 : 1 }}>
              ⬇ Download
            </button>
            <div style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'monospace',
              color: logsLoading ? 'var(--brand)' : logsError ? 'var(--color-danger-500)' : 'var(--color-success-500)' }}>
              {logsLoading ? '⟳ Loading...' : logsError ? `✗ ${logsError}` : logs ? `✓ ${logs.split('\n').length} lines` : ''}
            </div>
          </div>
          {/* Log content */}
          <div ref={logsRef} style={{ flex: 1, overflowY: 'auto', height: 'calc(100% - 45px)',
            padding: '8px 0', fontFamily: "'Courier New', monospace", fontSize: 12.5, lineHeight: 1.6 }}>
            {logsLoading && (
              <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px' }}>Fetching logs...</div>
            )}
            {!logsLoading && logsError && !logs && (
              <div style={{ color: 'var(--color-danger-500)', padding: '20px', fontFamily: 'monospace', fontSize: 12 }}>
                {logsError}
                {!previous && (
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setPrevious(true); fetchLogs(tail, true); }}
                      style={{ padding: '5px 14px', background: 'var(--color-warning-500)', color: 'var(--ink)',
                        border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      ⚡ Try previous container logs
                    </button>
                  </div>
                )}
              </div>
            )}
            {!logsLoading && !logs && !logsError && (
              <div style={{ color: '#484f58', textAlign: 'center', padding: '40px 20px', fontSize: 13 }}>
                No logs available.
                {!previous && (
                  <div style={{ marginTop: 10, fontSize: 12 }}>
                    Pod crashing?{' '}
                    <span onClick={() => { setPrevious(true); fetchLogs(tail, true); }}
                      style={{ color: 'var(--color-warning-500)', cursor: 'pointer', textDecoration: 'underline' }}>
                      View previous container logs
                    </span>
                  </div>
                )}
              </div>
            )}
            {!logsLoading && logs && logs.split('\n').map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '0 12px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ color: '#3d444d', minWidth: 38, textAlign: 'right',
                  userSelect: 'none', flexShrink: 0, fontSize: 11 }}>{i + 1}</span>
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  color: /error|Error|ERROR/.test(line) ? 'var(--color-danger-500)'
                       : /warn|Warn|WARN/.test(line)  ? 'var(--color-warning-500)' : 'var(--border)' }}>
                  {line}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TERMINAL TAB ── */}
      {tab === 'terminal' && (
        <div style={{ background: 'var(--ink)', borderRadius: 10, overflow: 'hidden', height: 'calc(100vh - 280px)',
          display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-2)',
            fontSize: 12, color: 'var(--muted-2)', fontFamily: 'monospace' }}>
            Connected to <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>{instance.name}</span>
            {' '}· namespace: <span style={{ color: 'var(--brand)' }}>{instance.namespace}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, fontFamily: 'monospace', fontSize: 12.5 }}>
            {termHistory.length === 0 && (
              <div style={{ color: 'var(--ink-2)' }}>Type a command below. Press Enter to run.</div>
            )}
            {termHistory.map((h, i) => (
              <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 2,
                color: h.type === 'input' ? 'var(--color-success-500)' : h.type === 'error' ? 'var(--color-danger-500)' : 'var(--border)' }}>
                {h.text}
              </div>
            ))}
            {termRunning && <div style={{ color: 'var(--ink-2)' }}>Running...</div>}
            <div ref={termBottomRef} />
          </div>
          <div style={{ background: 'var(--ink-2)', padding: '8px 14px', display: 'flex', gap: 8 }}>
            <span style={{ color: 'var(--color-success-500)', fontFamily: 'monospace', fontSize: 14, lineHeight: '32px' }}>$</span>
            <input
              autoFocus
              value={termInput}
              onChange={e => setTermInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !termRunning && handleTermExec()}
              placeholder="ls / or env or ps aux"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--border)', fontFamily: 'monospace', fontSize: 13 }}
            />
            <button onClick={handleTermExec} disabled={termRunning}
              style={{ padding: '4px 14px', background: 'var(--color-success-500)', color: '#fff',
                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
              Run
            </button>
            <button onClick={() => setTermHistory([])}
              style={{ padding: '4px 10px', background: 'var(--ink-2)', color: 'var(--border)',
                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ── EDIT TAB ── */}
      {tab === 'edit' && editForm && (
        <div className="card" style={{ maxWidth: 560 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 20 }}>Edit Instance Configuration</div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Container Image
            </label>
            <input className="input" value={editForm.image} onChange={e => setEditForm(f => ({...f, image: e.target.value}))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              ['cpu_request', 'CPU Request'],
              ['cpu_limit',   'CPU Limit'],
              ['mem_request_gb', 'RAM Request (GB)'],
              ['mem_limit_gb',   'RAM Limit (GB)'],
            ].map(([key, label]) => (
              <div key={key}>
                <label style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {label}
                </label>
                <input className="input" type="number" step="0.5" min="0.1" value={editForm[key]}
                  onChange={e => setEditForm(f => ({...f, [key]: e.target.value}))} />
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Storage GB (emptyDir)
            </label>
            <input className="input" type="number" value={editForm.storage_gb} onChange={e => setEditForm(f => ({...f, storage_gb: e.target.value}))} />
          </div>

          {saveMsg && (
            <div className={`banner ${saveMsg.includes('failed') ? 'danger' : 'success'}`} style={{ marginBottom: 16 }}>
              {saveMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSaveEdit} disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>
              {saving ? 'Saving...' : '💾 Save & Restart'}
            </button>
            <button onClick={() => setTab('overview')} className="btn btn-secondary">
              Cancel
            </button>
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 10 }}>
            Note: Raw pod resource changes require a pod restart to take effect.
          </p>
        </div>
      )}

      {tab === 'yaml' && (
        <div className="card">
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 20 }}>YAML Deploy History</div>

          {manifestHistoryLoading && (
            <div style={{ fontSize: 13, color: 'var(--muted-2)' }}>Loading…</div>
          )}

          {!manifestHistoryLoading && manifestHistory && manifestHistory.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted-2)' }}>No history yet.</div>
          )}

          {!manifestHistoryLoading && manifestHistory && manifestHistory.map(m => (
            <details key={m.id} style={{ marginBottom: 10, border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
              <summary style={{ cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                  color: m.success ? 'var(--color-success-700, #1b7f3b)' : 'var(--color-danger-700, #b52a2a)',
                  background: m.success ? 'var(--color-success-50, #eafaf0)' : 'var(--color-danger-50, #fdecea)',
                }}>
                  {m.success ? 'success' : 'failed'}
                </span>
                <span style={{ fontWeight: 600 }}>{m.action}</span>
                <span style={{ color: 'var(--muted-2)' }}>
                  {m.applied_at ? new Date(m.applied_at).toLocaleString() : ''}
                </span>
              </summary>
              <pre style={{
                fontSize: 11, fontFamily: 'monospace', background: 'var(--surface-2)',
                padding: 10, borderRadius: 6, marginTop: 10, maxHeight: 400, overflow: 'auto',
              }}>{m.yaml}</pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
